import { FocusElement, navigationService } from 'vue-spatialnavigation';
import { toDesignPx } from '@/helpers/scale';

export interface FocusOptions {
  //Scroll after focus
  scroll?: boolean;
}


//Where we should center on - not exactly the middle to read better
const CENTRE_RATIO = 1 / 2.5;

//Get nearest parent that scrolls vertically
function nearestParentThatScrolls(element: HTMLElement): HTMLElement | null {
  for (let parent = element.parentElement; parent; parent = parent.parentElement) {
    const { overflowY } = getComputedStyle(parent);

    if ((overflowY === 'auto' || overflowY === 'scroll') && parent.scrollHeight > parent.clientHeight) {
      return parent;
    }
  }

  return null;
}

function centreInView(element: HTMLElement): void {
  const scrollableParent = nearestParentThatScrolls(element);

  if (!scrollableParent) {
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return;
  }

  const rect = element.getBoundingClientRect();
  const portRect = scrollableParent.getBoundingClientRect();
  const centreOfElement = toDesignPx(rect.top - portRect.top + rect.height / 2);
  scrollableParent.scrollTo({
    top: scrollableParent.scrollTop + centreOfElement - scrollableParent.clientHeight * CENTRE_RATIO,
    behavior: 'smooth',
  });
}


function focusWithoutScroll(focusElement: FocusElement, element: HTMLElement | null): void {
  //If no HTML element, just focus normally
  if (!element) {
    focusElement.focus();
    return;
  }
  //Copy native focus function
  const nativeFocus = element.focus.bind(element);
  //Hijack focus to prevent scrolling
  element.focus = (options?: { preventScroll?: boolean }) =>
    nativeFocus({ ...options, preventScroll: true });

  try {
    //Focus without scrolling
    focusElement.focus();
  } finally {
    //Remove the hijacked focus function
    Reflect.deleteProperty(element, 'focus');
  }
}

/**
 * Whether the app has lost focus entirely — no element the d-pad can move from.
 *
 * The navigation service's focused element is the thing asked, not
 * `document.activeElement`: that element is what the d-pad moves from, and it
 * survives a deliberate DOM blur, so dismissing a platform keyboard does not
 * read as lost focus. An unmounted element is deregistered and leaves the
 * service with nothing focused, which does.
 *
 * Worth detecting because `vue-spatialnavigation` cannot recover on its own:
 * its fallback wants either the last focused id still registered or an element
 * marked `data-default`, and failing both, every key press is a no-op.
 */
export function nothingHasFocus(): boolean {
  const focused = navigationService.getFocusElementInFocus();
  if (!focused) return true;

  const element = document.getElementById(focused.id);
  return !element || !document.body.contains(element);
}

export function focusElementById(id: string, options: FocusOptions = {}): boolean {
  const focusElement = navigationService.getFocusElementById(id);
  if (!focusElement) return false;

  const element = document.getElementById(id);

  // `FocusElement.focus()` blurs whatever held focus already, so there is no
  // blur to do here: doing one routes focus through <body> on the way.
  focusWithoutScroll(focusElement, element);

  if (options.scroll && element) centreInView(element);

  return true;
}
